import { EstablishmentLeadsFunnelUpdateValidationSchema, updateSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { commercialKindOptions } from "../constants/commercialKindOptions.constant";
import { simpleNationalOptantOptions } from "@/common/constants/simpleNationalOptantOptions.constant";
import { BankInstitutionsTransformer } from "@/common/services/bankInstitutionsTransformer";
import { formatDateInLocalTimezone, formatPercentage } from "@/common/utils/formatters";
import { EstablishmentInfrastructuresTransformer } from "@/features/maintenance/establishmentInfrastructures/services/establishmentInfrastructuresTransformer";
import { EstablishmentTechnicalProfessionalsTransformer } from "@/features/maintenance/establishmentTechnicalProfessionals/services/establishmentTechnicalProfessionalsTransformer";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";

export const useEstablishmentLeadForm = ({
  establishmentLead
}: {
  establishmentLead: EstablishmentLeadData
}) => {
  return useForm<EstablishmentLeadsFunnelUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      commercial_manager: establishmentLead?.commercial_manager ? {
        label: establishmentLead?.commercial_manager.full_name,
        value: establishmentLead?.commercial_manager.id,
      } : undefined,
      commercial_manager_substitute: establishmentLead?.commercial_manager_substitute ? {
        label: establishmentLead?.commercial_manager_substitute.full_name,
        value: establishmentLead?.commercial_manager_substitute.id,
      } : undefined,
      accreditation_information: {
        product_categories: establishmentLead?.accreditation_information?.accreditation_information_product_categories?.map((category) => ({
          label: category.product_category.display_name,
          value: category.product_category.id,
        })),
        capture_methods: establishmentLead?.accreditation_information?.accreditation_information_capture_methods?.map((method) => ({
          label: method.capture_method.display_name,
          value: method.capture_method.id,
          perma_name: method.capture_method.perma_name,
        })),
        photos: establishmentLead?.accreditation_information?.photos?.map((photo) => ({
          id: photo.id,
          file: photo?.url,
        })),
        accreditation_information_pos_machines: {
          machine_id: establishmentLead?.accreditation_information?.accreditation_information_pos_machines?.map((machine) => ({
            label: `${machine.machine.reference_number} (${machine.machine.model.display_name})`,
            value: machine.machine.id,
          }))
        },
        authorizer_identifier: establishmentLead?.authorizer_identifier,
        payment_advance_daily_interest: establishmentLead.accreditation_information.payment_advance_daily_interest ? formatPercentage(Number(establishmentLead.accreditation_information.payment_advance_daily_interest)) : '',
      },
      general_information: {
        ...establishmentLead?.general_information,
        commercial_kind: establishmentLead.general_information.commercial_kind
          ? commercialKindOptions.find(
            ({ value }) =>
              value === establishmentLead.general_information.commercial_kind
          )
          : undefined,
        flag: establishmentLead?.general_information?.flag?.id ? {
          label: establishmentLead?.general_information?.flag.display_name,
          value: establishmentLead?.general_information?.flag.id,
        } : undefined,
        simple_national_optant: simpleNationalOptantOptions.find(
          ({ value }) => value === `${establishmentLead?.general_information?.simple_national_optant}`,
        ),
      },
      address_attributes: {
        ...establishmentLead?.address,
      },
      bank_account_information: {
        ...establishmentLead?.bank_account_information,
        bank_institution: establishmentLead?.bank_account_information?.bank_institution
          ? BankInstitutionsTransformer.optionsTransformer()([
            establishmentLead?.bank_account_information?.bank_institution,
          ])[0]
          : undefined,
      },
      ca_module_enabled: establishmentLead.ca_module_enabled,
      cm_module_enabled: establishmentLead.cm_module_enabled,
      tow_service_available: establishmentLead.tow_service_available,
      maintenance_infrastructures: EstablishmentInfrastructuresTransformer.optionsTransformer()(
        establishmentLead.maintenance_infrastructures?.map(({ infrastructure }) => infrastructure),
      ),
      maintenance_technical_professionals: EstablishmentTechnicalProfessionalsTransformer.optionsTransformer()(
        establishmentLead.maintenance_technical_professionals?.map(({ technical_professional }) => technical_professional),
      ),
      maintenance_specialty: establishmentLead.maintenance_specialty ? {
        id: establishmentLead.maintenance_specialty.id,
      } : undefined,
      maintenance_required_documents_attributes: establishmentLead.maintenance_required_documents.map((maintenanceRequiredDocument) => ({
        id: maintenanceRequiredDocument.id,
        document: {
          id: maintenanceRequiredDocument.document_id,
          display_name: maintenanceRequiredDocument.document.display_name,
        },
        file_attachment_attributes: {
          file_name: maintenanceRequiredDocument.file
            ? getFilenameFromUrl(maintenanceRequiredDocument.file?.url)
            : undefined,
          file_url: maintenanceRequiredDocument.file?.url,
        },
        valid_until: maintenanceRequiredDocument.valid_until
          ? formatDateInLocalTimezone(maintenanceRequiredDocument.valid_until)
          : undefined,
        issue_date: maintenanceRequiredDocument.issue_date
          ? formatDateInLocalTimezone(maintenanceRequiredDocument.issue_date)
          : undefined,
      })),
    }
  })
}
